import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Button,
  Col,
  DatePicker,
  Drawer,
  Form,
  Input,
  InputNumber,
  List,
  Popconfirm,
  Row,
  Segmented,
  Select,
  Space,
  Switch,
  Tag,
  Typography
} from "antd";
import { DeleteOutlined, FilePdfOutlined, PaperClipOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import { Success } from "../../../commons/Messages/Messages";
import { useIsMobile } from "../../../design-system/useMediaQuery";
import { shrinkPhoto } from "../workReports/photos";
import { formatDay } from "../workReports/format";
import {
  createInstallation,
  deleteInstallation,
  deleteInstallationFile,
  getInstallation,
  installationFileUrl,
  updateInstallation,
  uploadInstallationFile
} from "./api";
import { RESULT_COLORS, resultLabels, typeLabels, visitStatusLabels } from "./labels";

const toDay = value => (value ? dayjs(value) : null);
const fromDay = value => (value ? value.format("YYYY-MM-DD") : "");

/** Create or edit an Anlage; `id` null = new (optionally for `client`). */
const InstallationDrawer = ({ open, id, meta, defaults, onClose, onSaved }) => {
  const { t } = useTranslation("dashboard");
  const isMobile = useIsMobile();
  const [form] = Form.useForm();
  const fileInput = useRef(null);
  const [record, setRecord] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const client = Form.useWatch("client", form);
  const type = Form.useWatch("type", form);
  const types = typeLabels(t);
  const results = resultLabels(t);
  const statuses = visitStatusLabels(t);
  const canEdit = id ? meta?.can.update : meta?.can.create;

  const fill = data =>
    form.setFieldsValue({
      ...data,
      installDate: toDay(data.installDate),
      nextDue: toDay(data.nextDue),
      clientBranch: data.clientBranch || null
    });

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    setRecord(null);
    if (id) {
      getInstallation(id)
        .then(res => {
          setRecord(res.data.data);
          fill(res.data.data);
        })
        .catch(generalErrorAlert);
    } else {
      form.setFieldsValue({
        type: "heating_gas",
        intervalMonths: 12,
        dueMode: "completion",
        template: meta?.defaultTemplates?.heating_gas,
        ...defaults
      });
    }
  }, [open, id]);

  const clientInfo = meta?.clients.find(c => c._id === client);

  const submit = async values => {
    setBusy(true);
    try {
      const body = {
        ...values,
        installDate: fromDay(values.installDate),
        nextDue: fromDay(values.nextDue),
        clientBranch: values.clientBranch || null
      };
      const res = id ? await updateInstallation(id, body) : await createInstallation(body);
      Success(t("Saved"));
      onSaved(res.data.data);
    } catch (error) {
      generalErrorAlert(error);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    try {
      const res = await deleteInstallation(id);
      Success(res.data.retired ? t("Retired: it has protocols or a contract.") : t("Deleted"));
      onSaved(null);
    } catch (error) {
      generalErrorAlert(error);
    }
  };

  const upload = async event => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const isPdf = file.type === "application/pdf";
      const blob = isPdf ? file : (await shrinkPhoto(file)).blob;
      const res = await uploadInstallationFile(
        id,
        blob,
        isPdf ? file.name : blob.type === "image/png" ? "photo.png" : "photo.jpg"
      );
      setRecord(prev => ({ ...prev, files: res.data.data.files }));
    } catch (error) {
      generalErrorAlert(error);
    } finally {
      setUploading(false);
    }
  };

  const removeFile = async fileId => {
    try {
      const res = await deleteInstallationFile(id, fileId);
      setRecord(prev => ({ ...prev, files: res.data.data.files }));
    } catch (error) {
      generalErrorAlert(error);
    }
  };

  return (
    <Drawer
      rootClassName="maintenance-page"
      open={open}
      width={isMobile ? "100%" : 600}
      title={id ? record?.name || t("Installation") : t("New installation")}
      onClose={onClose}
      destroyOnClose
      footer={
        canEdit && (
          <div className="mnt-drawer-footer">
            {id && meta?.can.delete && (
              <Popconfirm title={t("Delete this installation?")} onConfirm={remove}>
                <Button danger icon={<DeleteOutlined />}>
                  {t("Delete")}
                </Button>
              </Popconfirm>
            )}
            <Button onClick={onClose}>{t("Cancel")}</Button>
            <Button type="primary" loading={busy} onClick={() => form.submit()}>
              {t("Save")}
            </Button>
          </div>
        )
      }
    >
      <Form form={form} layout="vertical" onFinish={submit} disabled={!canEdit}>
        <Row gutter={[12, 0]}>
          <Col xs={24} md={12}>
            <Form.Item name="client" label={t("Customer")} rules={[{ required: true }]}>
              <Select
                showSearch
                optionFilterProp="label"
                options={(meta?.clients || []).map(c => ({ value: c._id, label: c.name }))}
                onChange={() => form.setFieldsValue({ clientBranch: null })}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="clientBranch" label={t("Branch")}>
              <Select
                allowClear
                disabled={!clientInfo?.branches.length}
                placeholder={t("Main address")}
                options={(clientInfo?.branches || [])
                  .filter(b => b.active || b._id === record?.clientBranch)
                  .map(b => ({ value: b._id, label: b.name }))}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="type" label={t("Type")} rules={[{ required: true }]}>
              <Select
                options={(meta?.installationTypes || []).map(value => ({
                  value,
                  label: types[value]
                }))}
                onChange={value =>
                  !id && form.setFieldsValue({ template: meta?.defaultTemplates?.[value] })
                }
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="name" label={t("Name")} rules={[{ required: true, whitespace: true }]}>
              <Input maxLength={200} placeholder={t("e.g. Gas condensing boiler")} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="manufacturer" label={t("Manufacturer")}>
              <Input maxLength={120} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="model" label={t("Model")}>
              <Input maxLength={120} />
            </Form.Item>
          </Col>
          <Col xs={12}>
            <Form.Item name="serialNumber" label={t("Serial number")}>
              <Input maxLength={120} />
            </Form.Item>
          </Col>
          <Col xs={12}>
            <Form.Item name="inventoryNumber" label={t("Inventory number")}>
              <Input maxLength={120} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="location" label={t("Location on site")}>
              <Input maxLength={300} placeholder={t("e.g. Basement, boiler room")} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="installDate" label={t("Installed on")}>
              <DatePicker className="mnt-full" format="DD.MM.YYYY" />
            </Form.Item>
          </Col>
          <Col xs={24}>
            <Form.Item
              name="siteAddress"
              label={t("Site address")}
              extra={t("Only if it differs from the customer's or branch address.")}
            >
              <Input maxLength={500} />
            </Form.Item>
          </Col>
          <Col xs={24}>
            <Form.Item name="template" label={t("Checklist")}>
              <Select
                showSearch
                optionFilterProp="label"
                options={(meta?.templates || [])
                  .filter(
                    tpl =>
                      !tpl.installationTypes.length ||
                      tpl.installationTypes.includes(type) ||
                      tpl.key === form.getFieldValue("template")
                  )
                  .map(tpl => ({ value: tpl.key, label: tpl.title }))}
              />
            </Form.Item>
          </Col>
          <Col xs={12} md={8}>
            <Form.Item name="intervalMonths" label={t("Interval (months)")}>
              <InputNumber className="mnt-full" min={1} max={120} precision={0} />
            </Form.Item>
          </Col>
          <Col xs={12} md={8}>
            <Form.Item name="nextDue" label={t("Next due")}>
              <DatePicker className="mnt-full" format="DD.MM.YYYY" />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item name="dueMode" label={t("Next due counts from")}>
              <Segmented
                block
                options={[
                  { value: "completion", label: t("Inspection") },
                  { value: "schedule", label: t("Fixed rhythm") }
                ]}
              />
            </Form.Item>
          </Col>
          <Col xs={24}>
            <Form.Item name="notes" label={t("Notes")}>
              <Input.TextArea autoSize={{ minRows: 2, maxRows: 6 }} maxLength={5000} />
            </Form.Item>
          </Col>
          {id && (
            <Col xs={24}>
              <Form.Item name="active" label={t("Active")} valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
          )}
        </Row>
      </Form>

      {record?.contract && (
        <Typography.Paragraph type="secondary">
          {t("Covered by contract {{number}}: the contract sets the due dates.", {
            number: record.contract.number
          })}
        </Typography.Paragraph>
      )}

      {id && record && (
        <>
          <Typography.Title level={5}>{t("Photos and documents")}</Typography.Title>
          <List
            size="small"
            dataSource={record.files || []}
            locale={{ emptyText: t("No files yet") }}
            renderItem={file => (
              <List.Item
                actions={
                  meta?.can.update
                    ? [
                        <Button
                          key="del"
                          type="text"
                          danger
                          aria-label={t("Remove")}
                          icon={<DeleteOutlined />}
                          onClick={() => removeFile(file._id)}
                        />
                      ]
                    : []
                }
              >
                <a href={installationFileUrl(id, file._id)} target="_blank" rel="noreferrer">
                  {file.mimeType === "application/pdf" ? (
                    <FilePdfOutlined />
                  ) : (
                    <PaperClipOutlined />
                  )}{" "}
                  {file.caption || file.name}
                </a>
              </List.Item>
            )}
          />
          {meta?.can.update && (
            <>
              <input
                ref={fileInput}
                type="file"
                accept="image/*,application/pdf"
                hidden
                onChange={upload}
              />
              <Button
                className="mnt-top"
                icon={<PaperClipOutlined />}
                loading={uploading}
                onClick={() => fileInput.current?.click()}
              >
                {t("Add photo or PDF")}
              </Button>
            </>
          )}

          <Typography.Title level={5} className="mnt-section">
            {t("Inspection history")}
          </Typography.Title>
          <List
            size="small"
            dataSource={record.visits || []}
            locale={{ emptyText: t("No inspections yet") }}
            renderItem={visit => (
              <List.Item>
                <Space wrap>
                  <Link to={`/user/maintenance/visits/${visit._id}`}>{visit.number}</Link>
                  <span>{formatDay(visit.date)}</span>
                  <Tag>{statuses[visit.status]}</Tag>
                  {visit.result && (
                    <Tag color={RESULT_COLORS[visit.result]}>{results[visit.result]}</Tag>
                  )}
                </Space>
              </List.Item>
            )}
          />
        </>
      )}
    </Drawer>
  );
};

export default InstallationDrawer;
